import { useEffect, useRef, useState } from 'react';

const dishes = [
  { id: 'pulled-pork', name: 'Pulled Pork', alt: 'Heartland pulled pork sandwich with barbecue sauce and crinkle-cut fries' },
  { id: 'pork-steak', name: 'Pork Steak', alt: 'AI-expanded presentation of Heartland barbecue pork steak with beans, green beans and a roll' },
  { id: 'meatloaf', name: 'Meatloaf', alt: 'Heartland meatloaf with mashed potatoes, gravy and Brussels sprouts' }
];

export function FoodGallery() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(true);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [visible, setVisible] = useState(true);
  const [failed, setFailed] = useState<string[]>([]);
  const touchStart = useRef<number | null>(null);
  useEffect(() => {
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPaused(preference.matches);
    const reduce = () => { if (preference.matches) setPaused(true); };
    const visibility = () => setVisible(!document.hidden);
    preference.addEventListener('change', reduce);
    document.addEventListener('visibilitychange', visibility);
    visibility();
    return () => { preference.removeEventListener('change', reduce); document.removeEventListener('visibilitychange', visibility); };
  }, []);
  const rotating = !paused && !hovered && !focused && visible;
  useEffect(() => {
    if (!rotating) return;
    const timer = window.setInterval(() => setActive((index) => (index + 1) % dishes.length), 5000);
    return () => window.clearInterval(timer);
  }, [rotating]);
  const select = (index: number) => { setPaused(true); setActive((index + dishes.length) % dishes.length); };
  return (
    <div className="food-gallery" role="region" aria-roledescription="carousel" aria-label="Food from Heartland" aria-describedby="food-gallery-help" tabIndex={0}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
      onTouchStart={(event) => { touchStart.current = event.touches[0]?.clientX ?? null; }}
      onTouchEnd={(event) => { const x = event.changedTouches[0]?.clientX; if (touchStart.current !== null && x !== undefined && Math.abs(x - touchStart.current) > 45) select(active + (x < touchStart.current ? 1 : -1)); touchStart.current = null; }}
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); select(active - 1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); select(active + 1); }
        if (event.key === ' ') { event.preventDefault(); setPaused((value) => !value); }
      }}>
      <span id="food-gallery-help" className="food-gallery-sr-only">Photos rotate automatically. Hover or focus to hold a photo. Use left and right arrow keys or swipe to change photos. Space toggles automatic rotation, which resumes after leaving the gallery.</span>
      <div className="food-gallery-frame" aria-live={rotating ? 'off' : 'polite'} aria-atomic="true">
        {dishes.map((dish, index) => <figure key={dish.id} className={`food-gallery-slide food-gallery-${dish.id}${active === index ? ' is-active' : ''}`}
          role="group" aria-roledescription="slide" aria-label={`${index + 1} of ${dishes.length}: ${dish.name}`} aria-hidden={active !== index}>
          {failed.includes(dish.id) ? <div className="food-gallery-fallback">{dish.name}<span>Photo unavailable</span></div> : <img
            src={`/images/food-gallery/${dish.id === 'pork-steak' ? 'pork-steak-wide' : dish.id}.webp`} alt={dish.alt} width={dish.id === 'pork-steak' ? 1200 : 1280} height={dish.id === 'pork-steak' ? 900 : 1280}
            fetchPriority={index === 0 ? 'high' : 'auto'} decoding="async"
            onError={() => setFailed((previous) => previous.includes(dish.id) ? previous : [...previous, dish.id])} />}
        </figure>)}
      </div>
      <div className="food-gallery-caption" aria-hidden="true"><strong>{dishes[active].name}</strong></div>
    </div>
  );
}
